import { useRef } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { Layout } from '../../components/Layout.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import { AuditLogPane } from './panes/AuditLogPane.js';
import { CategoriesPane } from './panes/CategoriesPane.js';
import { ReportingPeriodsPane } from './panes/ReportingPeriodsPane.js';
import { TeamsPane } from './panes/TeamsPane.js';
import { UsersPane } from './panes/UsersPane.js';
import './admin.css';

type AdminTab = 'teams' | 'users' | 'categories' | 'audit-log' | 'reporting-periods';

const TABS: { id: AdminTab; label: string }[] = [
  { id: 'teams', label: 'Teams' },
  { id: 'users', label: 'Users & Roles' },
  { id: 'categories', label: 'Categories & Classifications' },
  { id: 'audit-log', label: 'Audit log' },
  { id: 'reporting-periods', label: 'Reporting periods' },
];

const VALID_TABS = new Set<string>(TABS.map((t) => t.id));

export function AdminPage() {
  const { tab } = useParams<{ tab: string }>();
  const navigate = useNavigate();
  const tabRefs = useRef<Map<AdminTab, HTMLButtonElement>>(new Map());

  const activeTab = (VALID_TABS.has(tab ?? '') ? tab : null) as AdminTab | null;

  usePageTitle('Administration');

  if (!activeTab) {
    return <Navigate to="/admin/teams" replace />;
  }

  function handleTabClick(id: AdminTab) {
    void navigate(`/admin/${id}`);
  }

  function handleTabKeyDown(e: React.KeyboardEvent, currentId: AdminTab) {
    const ids = TABS.map((t) => t.id);
    const idx = ids.indexOf(currentId);
    let next: AdminTab | null = null;
    if (e.key === 'ArrowRight') {
      next = ids[(idx + 1) % ids.length] ?? null;
    } else if (e.key === 'ArrowLeft') {
      next = ids[(idx - 1 + ids.length) % ids.length] ?? null;
    }
    if (next) {
      e.preventDefault();
      void navigate(`/admin/${next}`);
      tabRefs.current.get(next)?.focus();
    }
  }

  return (
    <Layout pageTitle="Administration">
      <div className="page-header">
        <h1>Administration</h1>
      </div>

      <div className="admin-subtabs" role="tablist" aria-label="Administration sections">
        {TABS.map((t) => (
          <button
            key={t.id}
            ref={(el) => {
              if (el) tabRefs.current.set(t.id, el);
            }}
            type="button"
            role="tab"
            id={`admin-tab-${t.id}`}
            aria-selected={activeTab === t.id}
            aria-controls={`admin-tab-panel-${t.id}`}
            tabIndex={activeTab === t.id ? 0 : -1}
            className={`sub-tab${activeTab === t.id ? ' active' : ''}`}
            onClick={() => handleTabClick(t.id)}
            onKeyDown={(e) => handleTabKeyDown(e, t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div
        id={`admin-tab-panel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`admin-tab-${activeTab}`}
        tabIndex={0}
      >
        {activeTab === 'teams' && <TeamsPane />}
        {activeTab === 'users' && (
          <UsersPane
            onNavigate={(dest) => {
              if (dest === 'teams') void navigate('/admin/teams');
              else if (dest === 'projects') void navigate('/projects');
            }}
          />
        )}
        {activeTab === 'categories' && <CategoriesPane />}
        {activeTab === 'audit-log' && <AuditLogPane />}
        {activeTab === 'reporting-periods' && <ReportingPeriodsPane />}
      </div>
    </Layout>
  );
}
